import React from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { imageUrl } from '../lib/images'

export default function OmOsPage() {
  return (
    <>
      <SEO
        title="Om Havdrup Rengøring"
        description="Lær Havdrup Rengøring at kende. Grundig og fleksibel rengøring tilpasset dine behov med vægt på kvalitet, ordentlighed og gode aftaler."
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#0d3836]/10 to-white py-12 sm:py-16 lg:py-20 border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="mb-6 flex items-center gap-2 text-xs text-slate-500" aria-label="Brødkrummer">
            <Link to="/" className="hover:text-slate-800 transition-colors">Forside</Link>
            <i className="fa-regular fa-chevron-right text-[10px]" aria-hidden="true" />
            <span className="font-semibold text-slate-900">Om Havdrup Rengøring</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#0d3836]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#0d3836]">
                <i className="fa-regular fa-shield-check text-[#0d3836]" aria-hidden="true" />
                Vores værdier & tilgang
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 font-display">
                Lidt om Havdrup Rengøring
              </h1>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                Hos Havdrup Rengøring får du grundig og fleksibel rengøring tilpasset dine behov. Vi hjælper både private, virksomheder og klinikker og lægger vægt på kvalitet, ordentlighed og gode aftaler.
              </p>
              <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
                <Link
                  to="/kontakt"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[#0d3836] px-6 py-3.5 text-sm font-semibold text-white shadow-xs hover:bg-[#154e4a] transition-colors cursor-pointer text-center"
                >
                  <span>Kontakt os for en snak</span>
                  <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href="tel:31341632"
                  className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors text-center"
                >
                  <i className="fa-regular fa-phone text-[#0d3836]" aria-hidden="true" />
                  <span className="no-break">31 34 16 32</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden rounded-2xl bg-slate-100 shadow-md border border-slate-200/80 aspect-4/3">
                <img
                  src={imageUrl('/_assets/shutterstock-2479566829.jpg', 'width=800,quality=85,format=auto')}
                  alt="Professionelle rengøringsredskaber, spand og støvsuger"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Primær tekst & præsentation */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Tekstindhold */}
            <div className="lg:col-span-8 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-display mb-4">
                  Vi gør os umage med hver eneste opgave
                </h2>
                <div className="space-y-4 text-slate-700 leading-relaxed text-sm sm:text-base">
                  <p>
                    Vi tilpasser rengøringen efter den enkelte kundes behov og aftaler. Vi er fleksible og gør os umage med hver eneste opgave – uanset om det drejer sig om en mindre privat bolig eller større erhvervslokaler.
                  </p>
                  <p>
                    Vi ved, at behovet for rengøring ikke er ens fra kunde til kunde. Derfor tager vi udgangspunkt i den konkrete opgave og laver en aftale, der passer til både lokalerne og dine ønsker.
                  </p>
                  <p>
                    For os handler god rengøring ikke kun om et flot resultat. Det handler også om at være til at stole på, overholde de aftaler der bliver lavet og gøre sig umage – hver gang.
                  </p>
                  <p>
                    Vi hjælper både private kunder og virksomheder og kan tage os af såvel løbende rengøring som enkeltstående, mere omfattende opgaver.
                  </p>
                </div>
              </div>

              {/* Ydelserne vi tilbyder */}
              <div className="rounded-2xl border border-[#0d3836]/15 bg-[#0d3836]/5 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-slate-900 font-display mb-3">
                  Vi tilbyder blandt andet:
                </h3>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-800">
                  <li className="flex items-center gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836]" aria-hidden="true" />
                    <span>Privat rengøring</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836]" aria-hidden="true" />
                    <span>Erhvervsrengøring</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836]" aria-hidden="true" />
                    <span>Hovedrengøring</span>
                  </li>
                  <li className="flex items-center gap-2.5">
                    <i className="fa-regular fa-check text-[#0d3836]" aria-hidden="true" />
                    <span>Flytterengøring</span>
                  </li>
                  <li className="flex items-center gap-2.5 sm:col-span-2 font-medium text-slate-900">
                    <i className="fa-regular fa-star text-[#0d3836]" aria-hidden="true" />
                    <span>Rengøring af klinikker – vi har mest erfaring indenfor klinikker</span>
                  </li>
                </ul>
              </div>

              {/* Vores værdier */}
              <div>
                <h3 className="text-xl font-bold tracking-tight text-slate-900 font-display mb-4">
                  Hvad du kan forvente af Havdrup Rengøring
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-slate-200 p-5 bg-white">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] mb-3">
                      <i className="fa-regular fa-sparkles text-lg" aria-hidden="true" />
                    </div>
                    <h4 className="text-base font-bold text-slate-900 font-display mb-1">Grundighed</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Vi overser ikke detaljerne og sørger for, at overflader, paneler og kroge fremstår pæne og rene.
                    </p>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-5 bg-white">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] mb-3">
                      <i className="fa-regular fa-handshake text-lg" aria-hidden="true" />
                    </div>
                    <h4 className="text-base font-bold text-slate-900 font-display mb-1">Tillid & stabilitet</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Du overlader dit hjem eller arbejdsplads til en pålidelig partner. Vi overholder altid vores aftaler.
                    </p>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-5 bg-white">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] mb-3">
                      <i className="fa-regular fa-sliders text-lg" aria-hidden="true" />
                    </div>
                    <h4 className="text-base font-bold text-slate-900 font-display mb-1">Fleksibilitet</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Rengøringen planlægges, så den passer ind i din hverdag eller din virksomheds åbningstider.
                    </p>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-5 bg-white">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0d3836]/10 text-[#0d3836] mb-3">
                      <i className="fa-regular fa-location-dot text-lg" aria-hidden="true" />
                    </div>
                    <h4 className="text-base font-bold text-slate-900 font-display mb-1">Lokal nærhed</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Med base i Havdrup er vi tæt på og hurtige at komme i kontakt med for både private og virksomheder.
                    </p>
                  </div>
                </div>
              </div>

              {/* Priser forklaret */}
              <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-6 sm:p-8">
                <h3 className="text-lg font-bold text-slate-900 font-display mb-2">
                  Priserne tilpasses den konkrete opgave
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Priserne kan være forskellig da det er afhængig af opgavens størrelse, omfang osv. Vi tilpasser rengøringen efter den enkelte kundes behov og aftaler. Du får derfor altid et gennemskueligt og fair tilbud, som matcher præcis det, du har brug for.
                </p>
              </div>
            </div>

            {/* Sidebar med kontakt og profil */}
            <div className="lg:col-span-4 space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xs space-y-5">
                <h3 className="text-lg font-bold tracking-tight text-slate-900 font-display">
                  Virksomhedsoplysninger
                </h3>
                <div className="space-y-3.5 text-sm text-slate-600">
                  <div className="flex items-start gap-3">
                    <i className="fa-regular fa-building mt-0.5 text-[#0d3836] shrink-0" aria-hidden="true" />
                    <div>
                      <strong className="text-slate-900 block font-medium">Havdrup Rengøring</strong>
                      <span>Professionel rengøringsservice</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <i className="fa-regular fa-location-dot mt-0.5 text-[#0d3836] shrink-0" aria-hidden="true" />
                    <div>
                      <span>Sivvænget 6</span>
                      <p>4622 Havdrup</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-1 border-t border-slate-100">
                    <i className="fa-regular fa-phone text-[#0d3836] shrink-0" aria-hidden="true" />
                    <a
                      href="tel:31341632"
                      className="font-medium text-slate-900 hover:text-[#0d3836] transition-colors"
                    >
                      <span className="no-break">31 34 16 32</span>
                    </a>
                  </div>

                  <div className="flex items-center gap-3">
                    <i className="fa-regular fa-envelope text-[#0d3836] shrink-0" aria-hidden="true" />
                    <a
                      href="mailto:havdruprengoring@gmail.com"
                      className="text-slate-900 hover:text-[#0d3836] transition-colors text-xs sm:text-sm"
                    >
                      <span className="no-break">havdruprengoring@gmail.com</span>
                    </a>
                  </div>
                </div>

                <div className="pt-2">
                  <Link
                    to="/kontakt"
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#0d3836] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#154e4a] transition-colors cursor-pointer"
                  >
                    <span>Få et uforpligtende tilbud</span>
                    <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                </div>
              </div>

              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <img
                  src={imageUrl('/_assets/shutterstock-2301428911.jpg', 'width=600,quality=80,format=auto')}
                  alt="Professionel rengøring og specialiseret tekstilrens"
                  className="h-48 w-full object-cover"
                />
                <div className="p-4 text-xs text-slate-500">
                  Vi går ikke på kompromis med grundigheden – uanset opgavens størrelse.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA sektion */}
      <section className="py-14 bg-slate-50 border-t border-slate-100 text-center">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 space-y-4">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-display">
            Klar til et uforpligtende tilbud?
          </h2>
          <p className="text-slate-600 max-w-xl mx-auto text-sm sm:text-base">
            Uanset om du søger hjælp til dit private hjem, din virksomhed eller din klinik, tager vi gerne en god snak om mulighederne.
          </p>
          <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4">
            <Link
              to="/kontakt"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-[#0d3836] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#154e4a] transition-colors cursor-pointer text-center"
            >
              <span>Gå til kontakt</span>
              <i className="fa-regular fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
            <a
              href="tel:31341632"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors text-center"
            >
              <i className="fa-regular fa-phone text-[#0d3836]" aria-hidden="true" />
              <span className="no-break">Ring på 31 34 16 32</span>
            </a>
          </div>
        </div>
      </section>
    </>
  )
}
